import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Check, CalendarClock, CreditCard, ShieldCheck, ExternalLink } from "lucide-react";
import { Dialog, DialogContent, DialogTrigger, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import CallRequestForm from "./CallRequestForm";
import { createReservation, getWorkflowConfig, createCheckout } from "../../lib/adventureApi";
import { useMarket } from "../../lib/market";

const field = "h-11 rounded-sm bg-white border-sand-dark focus-visible:ring-terracotta";
const lbl = "font-mono text-[11px] uppercase tracking-widest text-ink-secondary mb-1.5 block";

function Body({ tripName, journeyId, tourSlug }) {
  const { market } = useMarket();
  const isAU = market === "au";
  const cur = isAU ? "A$" : "$";
  const [f, setF] = useState({ first_name: "", last_name: "", email: "", phone: "", phone_code: "+44" });
  const [cfg, setCfg] = useState({ deposit_amount: 500, au_deposit_amount: 500, wetravel_url: "" });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const depositAmount = isAU ? (cfg.au_deposit_amount || 500) : cfg.deposit_amount;

  useEffect(() => { getWorkflowConfig().then(setCfg).catch(() => {}); }, []);

  const reserve = async (option) => {
    if (!f.first_name || !f.email) { toast.error("Please add your name and email first"); return; }
    setBusy(true);
    try {
      const r = await createReservation({ journey_id: journeyId, tour_slug: tourSlug, trip_name: tripName, deposit_option: option, market, ...f });
      if (isAU && option === "now") {
        const c = await createCheckout({
          market: "au", tour_slug: tourSlug, trip_name: tripName, reservation_id: r.id,
          first_name: f.first_name, last_name: f.last_name, email: f.email, phone: f.phone,
          origin_url: window.location.origin,
        });
        if (c.checkout_url) { window.location.href = c.checkout_url; return; }
        toast.error("Could not start payment — please try again.");
        setBusy(false);
        return;
      }
      setResult({ ...r, option });
      if (!isAU && option === "now" && r.wetravel_url) window.open(r.wetravel_url, "_blank", "noopener");
    } catch { toast.error("Something went wrong — please try again."); }
    finally { setBusy(false); }
  };

  if (result) {
    const url = result.wetravel_url;
    return (
      <div className="p-8 text-center" data-testid="reserve-success">
        <div className="w-14 h-14 rounded-full bg-moss-light flex items-center justify-center mx-auto"><Check className="w-7 h-7 text-moss" /></div>
        <h3 className="font-display font-extrabold text-2xl mt-4">Your trip is reserved!</h3>
        {result.option === "now" ? (
          <p className="text-ink-secondary mt-2">Complete your <b>{cur}{depositAmount}</b> deposit to confirm your place.</p>
        ) : (
          <p className="text-ink-secondary mt-2">Your <b>{cur}{depositAmount}</b> deposit is due by <b>{result.deposit_due}</b>. We've emailed you a secure pay-link.</p>
        )}
        {!isAU && url ? (
          <a href={url} target="_blank" rel="noreferrer" data-testid="reserve-pay-link"
            className="mt-5 inline-flex items-center gap-2 bg-terracotta hover:bg-terracotta-hover text-white h-12 px-6 rounded-sm font-semibold transition-colors">
            <CreditCard className="w-4 h-4" /> Pay {cur}{depositAmount} deposit <ExternalLink className="w-4 h-4" />
          </a>
        ) : (
          <p className="text-[12px] text-ink-muted mt-4">Our team will email your secure payment link shortly.</p>
        )}
        <div className="mt-6 pt-5 border-t border-sand-dark">
          <CallRequestForm tripName={tripName}
            triggerClassName="inline-flex items-center gap-2 border border-moss text-moss hover:bg-moss-light h-11 px-5 rounded-sm font-semibold text-sm transition-colors" />
        </div>
      </div>
    );
  }

  return (
    <div className="max-h-[82vh] overflow-y-auto thin-scroll">
      <div className="bg-gradient-to-b from-[#221c17] to-[#3a2f26] px-6 py-5 text-white">
        <p className="font-mono text-[10px] uppercase tracking-widest text-white/60">Reserve this trip{isAU ? " · Australia 🇦🇺" : ""}</p>
        <h3 className="font-display font-extrabold text-xl tracking-tight mt-1">{tripName}</h3>
        <p className="text-white/70 text-sm mt-1 flex items-center gap-1.5"><ShieldCheck className="w-4 h-4" /> Secure {cur}{depositAmount} deposit · fully personalised digital itinerary</p>
      </div>

      <div className="p-6">
        <p className="font-display font-bold mb-3">Your details</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label className={lbl}>First name *</Label><Input data-testid="res-first" className={field} value={f.first_name} onChange={set("first_name")} /></div>
          <div><Label className={lbl}>Last name</Label><Input data-testid="res-last" className={field} value={f.last_name} onChange={set("last_name")} /></div>
          <div><Label className={lbl}>Email *</Label><Input data-testid="res-email" type="email" className={field} value={f.email} onChange={set("email")} placeholder="example@email.com" /></div>
          <div><Label className={lbl}>Phone</Label><Input data-testid="res-phone" className={field} value={f.phone} onChange={set("phone")} /></div>
        </div>

        <p className="font-display font-bold mt-6 mb-3">Choose how to secure your place</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <button onClick={() => reserve("now")} disabled={busy} data-testid="reserve-now-btn"
            className="text-left border-2 border-terracotta rounded-md p-4 hover:bg-terracotta/5 transition-colors disabled:opacity-60">
            <div className="flex items-center gap-2 text-terracotta"><CreditCard className="w-5 h-5" /><span className="font-display font-bold">Pay {cur}{depositAmount} now</span></div>
            <p className="text-sm text-ink-secondary mt-2">{isAU ? "Confirm instantly via secure Stripe checkout (AUD)." : "Confirm instantly via WeTravel secure checkout."}</p>
          </button>
          <button onClick={() => reserve("7days")} disabled={busy} data-testid="reserve-later-btn"
            className="text-left border-2 border-sand-dark rounded-md p-4 hover:border-moss transition-colors disabled:opacity-60">
            <div className="flex items-center gap-2 text-moss"><CalendarClock className="w-5 h-5" /><span className="font-display font-bold">Reserve, pay in 7 days</span></div>
            <p className="text-sm text-ink-secondary mt-2">Hold your place now — we'll email a pay-link, deposit due within 7 days.</p>
          </button>
        </div>
        {busy && <p className="text-sm text-ink-muted mt-4 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> {isAU ? "Redirecting to secure checkout…" : "Reserving…"}</p>}

        <div className="mt-6 pt-5 border-t border-sand-dark flex items-center justify-between gap-3 flex-wrap">
          <p className="text-sm text-ink-secondary">Prefer to chat first?</p>
          <CallRequestForm tripName={tripName}
            triggerClassName="inline-flex items-center gap-2 border border-moss text-moss hover:bg-moss-light h-10 px-4 rounded-sm font-semibold text-sm transition-colors" />
        </div>
      </div>
    </div>
  );
}

export default function ReserveDialog({ tripName, journeyId, tourSlug, triggerClassName, triggerLabel = "Reserve this trip" }) {
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="reserve-open-btn" className={triggerClassName}>
          <ShieldCheck className="w-4 h-4" /> {triggerLabel}
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-xl w-[96vw] p-0 gap-0 overflow-hidden rounded-md" data-testid="reserve-modal" aria-describedby={undefined}>
        <DialogTitle className="sr-only">Reserve this trip</DialogTitle>
        {open && <Body tripName={tripName} journeyId={journeyId} tourSlug={tourSlug} />}
      </DialogContent>
    </Dialog>
  );
}
